import * as React from "react"

import { cn } from "@vaiu/utils"

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, onWheel, ...props }, ref) => {
    const hostRef = React.useRef<HTMLInputElement | null>(null)

    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        hostRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref != null) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = node
        }
      },
      [ref]
    )

    React.useEffect(() => {
      const el = hostRef.current
      if (!el || type !== "number") return

      const stopWheelChangingValue = (e: WheelEvent) => {
        if (document.activeElement === el) {
          e.preventDefault()
        }
      }

      el.addEventListener("wheel", stopWheelChangingValue, { passive: false })
      return () => el.removeEventListener("wheel", stopWheelChangingValue)
    }, [type])

    const handleWheel = React.useCallback(
      (e: React.WheelEvent<HTMLInputElement>) => {
        onWheel?.(e)
      },
      [onWheel]
    )

    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          type === "number" &&
            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
          className
        )}
        ref={setRefs}
        onWheel={handleWheel}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
